import { useState, useEffect } from "react";
import { Tabs, Tab, styled, CircularProgress } from "@mui/material";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import { useNavigate, useLocation } from "react-router-dom";
import actionsHandler from "@/store/actions/actionHandler";
import { EndpointType } from "@/constants/api";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import MasterDataTable, { MasterType } from "./components/MasterDataTable";

// Styled components for tabs
const StyledTabs = styled(Tabs)({
  minHeight: "unset",
  "& .MuiTabs-indicator": {
    display: "none",
  },
});

const StyledTab = styled(Tab)({
  minHeight: "unset",
  padding: "8px 16px",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 600,
  minWidth: "unset",
});

// Define tabs configuration
const tabs: Array<{ label: string; path: string; type: MasterType }> = [
  {
    label: "Onboarded Stakeholder",
    path: "/user-management/onboardedUsers",
    type: "onboardedUsers",
  },
  {
    label: "Invited Stakeholder",
    path: "/user-management/invitedUsers",
    type: "invitedUsers",
  },
  {
    label: "Profile Update Request",
    path: "/user-management/edit-requests",
    type: "editProfileRequests",
  },
];

interface FetchParams {
  limit?: number;
  page?: number;
  search?: string;
  sortColumn?: string;
  sortDirection?: string;
  companyId?: number | null;
}

const UserManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch<AppThunkDispatch>();

  useEffect(() => {
    setLoading(true);
    const currentPath = location.pathname;
    const tabIndex = tabs.findIndex((tab) => tab.path === currentPath);
    if (tabIndex !== -1 && tabIndex !== activeTab) {
      setActiveTab(tabIndex);
    }
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, [location]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    navigate(tabs[newValue].path);
  };

  const fetchMasterData = async (
    type: MasterType,
    params: FetchParams = {},
    isArchiveView: boolean = false,
  ) => {
    try {
      const endpoint =
        type === "onboardedUsers"
          ? "usersList"
          : type === "invitedUsers"
            ? "userByID"
            : "editProfileRequests";

      const { getAllData } = actionsHandler(endpoint as EndpointType);
      let ordering = "";
      if (params.sortColumn) {
        ordering =
          params.sortDirection === "desc"
            ? `-${params.sortColumn}`
            : params.sortColumn;
      }

      // Build custom query string
      let customQuery = ordering ? `ordering=${ordering}` : "";
      if (type === "onboardedUsers" && params.companyId) {
        customQuery += customQuery
          ? `&user_detail__companies__id=${params.companyId}`
          : `user_detail__companies__id=${params.companyId}`;
      }

      const requestParams = {
        limit: params.limit || 10,
        page: params.page || 1,
        search: params.search || "",
        customQuery,
        ...(type === "invitedUsers" && { customParams: ["invited/"] }),
      };

      const response = await dispatch(getAllData(requestParams));

      return response;
    } catch (error) {
      console.error(`Error fetching ${type} data:`, error);
      return {};
    }
  };

  return (
    <div className="tw-relative">
      {loading ? (
        <div className="tw-flex tw-justify-center tw-items-center tw-h-screen">
          <CircularProgress />
        </div>
      ) : (
        <>
          <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
            <div className="tw-px-6 tw-pt-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
              <div className="tw-flex tw-justify-between tw-items-center">
                <div className="tw-flex tw-items-center">
                  <img
                    src={BackIcon}
                    alt="Back Icon"
                    onClick={() => navigate("/menu")}
                    className="tw-cursor-pointer"
                  />
                  <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                    Stakeholder Management
                  </h1>
                </div>
              </div>

              <div className="tw-w-fit tw-mt-4 tw-border tw-border-[#E9EAEB]">
                <StyledTabs value={activeTab} onChange={handleTabChange}>
                  {tabs.map((tab, index) => (
                    <StyledTab
                      key={index}
                      label={tab.label}
                      className={`tw-text-sm tw-font-semibold ${
                        activeTab === index ? "active-tab" : "tw-text-[#232323]"
                      } tab-border`}
                    />
                  ))}
                </StyledTabs>
              </div>
            </div>
          </div>
          {/* Main Content */}
          <div className="tw-mt-[177px]">
            {activeTab !== null && (
              <MasterDataTable
                type={tabs[activeTab].type}
                fetchData={fetchMasterData}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UserManagement;
